import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Check, ChevronDown } from 'lucide-react'

import { track } from '@/api/tracking'
import { buildAppOutboundUrl } from '@/components/food-cost/buildAppOutboundUrl'
import {
    ASSETS,
    FOOD_COST_LANDING_PAGE,
    LANDING_DIRECT_CONVERSION_EXPERIMENT,
    OUTBOUND_TRACK_OPTIONS,
    PRICING_ALBARANES_INPUT_MAX,
    PRICING_AVG_ALBARANES_PER_MONTH,
    PRICING_INCLUDED_ALBARANES,
    PRICING_MONTHLY_BASE_FEE_EUR,
    PRICING_PER_ALBARAN_EUR,
    calcExtrasFeeFromAlbaranes,
    calcMonthlyFeeFromAlbaranes,
    getBillableAlbaranes,
} from '@/components/food-cost/constants'
import { formatOfferCountdownPart } from '@/components/leadFormOfferCountdown'
import { useLeadFormOfferCountdown } from '@/components/useLeadFormOfferCountdown'
import { useSectionViewTrack } from '@/hooks/useSectionViewTrack'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { ROUTES } from '@/navigation/constants'
import NumberFlow, { NumberFlowGroup, continuous } from '@number-flow/react'
import { getOrCreateDirectConversionOfferEndsAt } from '@tipi/shared'

const INCLUDE_KEYS = ['1', '2', '3', '4', '5'] as const

const NUMBER_FLOW_TIMING = { duration: 1400, easing: 'ease-out' } as const

interface PricingSectionProps {
    isDirectConversion?: boolean
}

const formatEur = (value: number, locale: string, fractionDigits?: number) => {
    const digits = fractionDigits ?? (Number.isInteger(value) ? 0 : 2)
    const formatted = new Intl.NumberFormat(locale.startsWith('en') ? 'en-GB' : 'es-ES', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    }).format(value)
    return `${formatted} €`
}

const getNumberLocale = (language: string) => (language.startsWith('en') ? 'en-GB' : 'es-ES')

interface SecondMonthCalculatorProps {
    variant: 'control' | 'treatment'
    locale: string
    numberLocale: string
    hasMounted: boolean
}

function SecondMonthCalculator({ variant, locale, numberLocale, hasMounted }: SecondMonthCalculatorProps) {
    const { t } = useTranslation()
    const albaranesInputId = useId()
    const calcPanelId = useId()
    const inputRef = useRef<HTMLInputElement>(null)
    const [isCalcOpen, setIsCalcOpen] = useState(false)
    const [albaranesInput, setAlbaranesInput] = useState('')

    const parsedAlbaranes = Number.parseInt(albaranesInput, 10)
    const hasValidCount = albaranesInput !== '' && Number.isFinite(parsedAlbaranes) && parsedAlbaranes >= 0
    const albaranesCount = hasValidCount ? Math.min(parsedAlbaranes, PRICING_ALBARANES_INPUT_MAX) : 0
    const extrasCount = getBillableAlbaranes(albaranesCount)
    const extrasTotal = calcExtrasFeeFromAlbaranes(albaranesCount)
    const monthlyTotal = calcMonthlyFeeFromAlbaranes(albaranesCount)
    const perAlbaranLabel = formatEur(PRICING_PER_ALBARAN_EUR, locale)
    const isWaiting = albaranesInput === ''
    const isTreatment = variant === 'treatment'

    useEffect(() => {
        if (!isCalcOpen) return
        inputRef.current?.focus()
    }, [isCalcOpen])

    const handleToggle = () => {
        setIsCalcOpen(open => !open)
    }

    return (
        <div
            className={cn(
                'flex min-w-0 flex-col',
                isTreatment
                    ? 'gap-[18px] rounded-[28px] border-[1.5px] border-[#1A1712] p-[30px]'
                    : 'gap-4 rounded-[1.25rem] border border-[#17130E] px-4 py-4',
            )}
        >
            <p
                className={cn(
                    'm-0 font-bold uppercase tracking-[0.14em]',
                    isTreatment ? 'text-base text-[#4A4339]' : 'text-[11px] text-[#5C5548]',
                )}
            >
                {t('foodCost.pricing.fromSecondMonth.label')}
            </p>
            <p className='mb-0 flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5'>
                <span
                    className={cn(
                        'font-semibold text-[#4A4339]',
                        isTreatment ? 'text-lg md:text-xl' : 'text-[15px] text-[#5C5548]',
                    )}
                >
                    {t('foodCost.pricing.fromSecondMonth.priceFrom')}
                </span>
                <span
                    className={cn(
                        'font-extrabold leading-none tracking-[-0.035em] text-[#1A1712]',
                        isTreatment ? 'text-[36px] md:text-[40px]' : 'text-[2rem] text-[#17130E]',
                    )}
                >
                    {t('foodCost.pricing.fromSecondMonth.price')}
                </span>
                <span
                    className={cn(
                        'font-semibold text-[#4A4339]',
                        isTreatment ? 'text-lg md:text-xl' : 'text-[14px] text-[#5C5548]',
                    )}
                >
                    {t('foodCost.pricing.fromSecondMonth.priceSuffix')}
                </span>
            </p>
            <p
                className={cn(
                    'mb-0 text-pretty leading-[1.5] text-[#4A4339]',
                    isTreatment ? 'text-[19px]' : 'text-[14px] leading-[1.45] text-[#5C5548]',
                )}
            >
                {t('foodCost.pricing.fromSecondMonth.descriptionBefore')}
                <strong className='font-bold text-[#1A1712]'>
                    {t('foodCost.pricing.fromSecondMonth.descriptionIncluded')}
                </strong>
                {t('foodCost.pricing.fromSecondMonth.descriptionMiddle')}
                <strong className='font-bold text-[#1A1712]'>
                    {t('foodCost.pricing.fromSecondMonth.descriptionPerUnit')}
                </strong>
                {t('foodCost.pricing.fromSecondMonth.descriptionAfter')}
            </p>

            <button
                type='button'
                aria-expanded={isCalcOpen}
                aria-controls={calcPanelId}
                onClick={handleToggle}
                className={cn(
                    'inline-flex items-center gap-2.5 self-start rounded-full border-[1.5px] border-[#1A1712] bg-transparent font-bold text-[#1A1712] transition-colors hover:bg-[#1A1712]/5',
                    isTreatment ? 'h-[54px] px-6 text-lg' : 'mt-0 gap-1.5 px-3.5 py-2 text-[13px] font-semibold',
                )}
            >
                {isCalcOpen
                    ? t('foodCost.pricing.fromSecondMonth.hideCalc')
                    : t('foodCost.pricing.fromSecondMonth.showCalc')}
                <ChevronDown
                    className={cn('duration-[250ms] h-4 w-4 shrink-0 transition-transform', isCalcOpen && 'rotate-180')}
                    strokeWidth={2.5}
                    aria-hidden
                />
            </button>

            {isCalcOpen ? (
                <div
                    id={calcPanelId}
                    className={cn(
                        'flex flex-col gap-4 border-t border-[rgba(26,23,18,0.15)] pt-[18px]',
                        !isTreatment && 'mt-0 gap-3 pt-4',
                    )}
                >
                    <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3'>
                        <div className='flex min-w-0 flex-col gap-0.5'>
                            <label
                                htmlFor={albaranesInputId}
                                className={cn(
                                    'font-semibold text-[#1A1712]',
                                    isTreatment ? 'text-lg' : 'text-[14px] font-bold leading-snug',
                                )}
                            >
                                {t('foodCost.pricing.fromSecondMonth.question')}
                            </label>
                            <p
                                className={cn(
                                    'm-0 text-[#6B6257]',
                                    isTreatment ? 'text-sm' : 'text-[12px] font-medium text-[#8A7F6F]',
                                )}
                            >
                                {t('foodCost.pricing.fromSecondMonth.avgNote', {
                                    count: PRICING_AVG_ALBARANES_PER_MONTH,
                                })}
                            </p>
                        </div>
                        <label
                            className={cn(
                                'flex h-14 w-full min-w-[160px] shrink-0 cursor-text items-center gap-2 rounded-xl border-2 border-[#1A1712] bg-[#FDFBF8] px-4 sm:w-auto',
                                isWaiting && 'animate-pricing-input-pulse',
                            )}
                        >
                            <input
                                ref={inputRef}
                                id={albaranesInputId}
                                type='text'
                                inputMode='numeric'
                                pattern='[0-9]*'
                                maxLength={3}
                                value={albaranesInput}
                                placeholder='0'
                                onChange={event => {
                                    const next = event.target.value
                                    if (next === '') {
                                        setAlbaranesInput('')
                                        return
                                    }
                                    if (!/^\d+$/.test(next)) return
                                    const asNumber = Number.parseInt(next, 10)
                                    setAlbaranesInput(String(Math.min(asNumber, PRICING_ALBARANES_INPUT_MAX)))
                                }}
                                aria-label={t('foodCost.pricing.fromSecondMonth.inputAriaLabel')}
                                className='w-16 min-w-0 bg-transparent text-2xl font-extrabold tabular-nums text-[#1A1712] caret-[#F4502A] outline-none placeholder:text-[rgba(26,23,18,0.35)]'
                            />
                            <span className='shrink-0 text-sm font-semibold text-[#6B6257]'>
                                {t('foodCost.pricing.fromSecondMonth.inputUnit')}
                            </span>
                        </label>
                    </div>

                    <NumberFlowGroup>
                        <div className='grid grid-cols-[1fr_auto_1fr_auto_1.2fr] items-stretch gap-2 max-[560px]:grid-cols-1 max-[560px]:gap-2'>
                            <div className='flex flex-col gap-1 rounded-2xl bg-[#FBF7F1] px-3 py-3.5'>
                                <p className='m-0 text-xs font-bold uppercase tracking-[0.08em] text-[#6B6257]'>
                                    {t('foodCost.pricing.fromSecondMonth.cuotaLabel')}
                                </p>
                                <p className='m-0 text-2xl font-extrabold tracking-[-0.02em] text-[#1A1712]'>
                                    {formatEur(PRICING_MONTHLY_BASE_FEE_EUR, locale)}
                                </p>
                                <p className='m-0 text-xs text-[#6B6257]'>
                                    {t('foodCost.pricing.fromSecondMonth.cuotaDetail', {
                                        count: PRICING_INCLUDED_ALBARANES,
                                    })}
                                </p>
                            </div>

                            <span
                                className='self-center text-[22px] font-bold text-[#A79D90] max-[560px]:hidden'
                                aria-hidden
                            >
                                +
                            </span>

                            <div className='flex flex-col gap-1 rounded-2xl bg-[#FBF7F1] px-3 py-3.5'>
                                <p className='m-0 text-xs font-bold uppercase tracking-[0.08em] text-[#6B6257]'>
                                    {t('foodCost.pricing.fromSecondMonth.extrasLabel')}
                                </p>
                                <p className='m-0 leading-none'>
                                    {hasMounted ? (
                                        <NumberFlow
                                            value={extrasTotal}
                                            locales={numberLocale}
                                            format={{
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            }}
                                            suffix=' €'
                                            plugins={[continuous]}
                                            transformTiming={NUMBER_FLOW_TIMING}
                                            spinTiming={NUMBER_FLOW_TIMING}
                                            className='text-2xl font-extrabold tracking-[-0.02em] text-[#F4502A] [--number-flow-mask-width:0em]'
                                        />
                                    ) : (
                                        <span className='text-2xl font-extrabold tracking-[-0.02em] text-[#F4502A]'>
                                            {formatEur(extrasTotal, locale, 2)}
                                        </span>
                                    )}
                                </p>
                                <p className='m-0 text-xs text-[#6B6257]'>
                                    {t('foodCost.pricing.fromSecondMonth.extrasDetail', {
                                        count: extrasCount,
                                        price: perAlbaranLabel,
                                    })}
                                </p>
                            </div>

                            <span
                                className='self-center text-[22px] font-bold text-[#A79D90] max-[560px]:hidden'
                                aria-hidden
                            >
                                =
                            </span>

                            <div className='flex flex-col gap-1 rounded-2xl bg-[#1A1712] px-3 py-3.5 text-[#F3ECE2]'>
                                <p className='m-0 text-xs font-bold uppercase tracking-[0.08em] opacity-70'>
                                    {t('foodCost.pricing.fromSecondMonth.totalLabel')}
                                </p>
                                <p className='m-0 leading-none'>
                                    {hasMounted ? (
                                        <NumberFlow
                                            value={monthlyTotal}
                                            locales={numberLocale}
                                            format={{
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            }}
                                            suffix=' €'
                                            plugins={[continuous]}
                                            transformTiming={NUMBER_FLOW_TIMING}
                                            spinTiming={NUMBER_FLOW_TIMING}
                                            className='text-[28px] font-extrabold tracking-[-0.035em] text-[#F3ECE2] [--number-flow-mask-width:0em]'
                                        />
                                    ) : (
                                        <span className='text-[28px] font-extrabold tracking-[-0.035em]'>
                                            {formatEur(monthlyTotal, locale, 2)}
                                        </span>
                                    )}
                                </p>
                                <p className='m-0 text-xs opacity-75'>
                                    {t('foodCost.pricing.fromSecondMonth.totalSuffix')}
                                </p>
                            </div>
                        </div>
                    </NumberFlowGroup>
                </div>
            ) : null}
        </div>
    )
}

export function PricingSection({ isDirectConversion = false }: PricingSectionProps) {
    const { t, i18n } = useTranslation()
    const pricingAppUrl = buildAppOutboundUrl(
        'pricing_cta',
        isDirectConversion ? { experiment: LANDING_DIRECT_CONVERSION_EXPERIMENT, variant: 'treatment' } : undefined,
    )
    const sectionRef = useRef(null)
    const [hasMounted, setHasMounted] = useState(false)
    const [offerEndsAt, setOfferEndsAt] = useState<Date | null>(() => {
        if (!isDirectConversion || typeof window === 'undefined') return null
        return getOrCreateDirectConversionOfferEndsAt()
    })
    const offerRemaining = useLeadFormOfferCountdown(isDirectConversion ? offerEndsAt : null)
    const showIntroOffer =
        isDirectConversion && offerEndsAt !== null && Boolean(offerRemaining && !offerRemaining.isExpired)

    const locale = i18n.language
    const numberLocale = getNumberLocale(locale)
    const offerHours = formatOfferCountdownPart(offerRemaining?.hours ?? 0)
    const offerMinutes = formatOfferCountdownPart(offerRemaining?.minutes ?? 0)
    const offerSeconds = formatOfferCountdownPart(offerRemaining?.seconds ?? 0)

    useEffect(() => {
        setHasMounted(true)
    }, [])

    useLayoutEffect(() => {
        if (!isDirectConversion) {
            setOfferEndsAt(null)
            return
        }
        const next = getOrCreateDirectConversionOfferEndsAt()
        setOfferEndsAt(current => (current?.getTime() === next.getTime() ? current : next))
    }, [isDirectConversion])

    useSectionViewTrack(sectionRef, 'Pricing', { landing_page: FOOD_COST_LANDING_PAGE })

    const trackPricingCta = () => {
        track(
            'Pricing CTA Clicked',
            {
                landing_page: FOOD_COST_LANDING_PAGE,
                cta: 'pricing_cta',
                destination_url: pricingAppUrl,
                ...(isDirectConversion ? { experiment: LANDING_DIRECT_CONVERSION_EXPERIMENT } : {}),
            },
            OUTBOUND_TRACK_OPTIONS,
        )
    }

    const trackCompanionshipLink = () => {
        track('Pricing CTA Clicked', {
            landing_page: FOOD_COST_LANDING_PAGE,
            cta: 'companionship_link',
            destination_url: ROUTES.ponloEnOrden,
        })
    }

    // The accompaniment landing is Spanish-only.
    const companionshipCard = locale.startsWith('es') ? (
        <div className='mx-[22px] mt-6 md:mx-auto md:mt-10 md:max-w-[1100px]'>
            <LocaleLink
                to={ROUTES.ponloEnOrden}
                onClick={trackCompanionshipLink}
                className='group relative flex flex-col gap-4 overflow-hidden rounded-[28px] bg-[#17130E] px-6 py-7 text-[#F5EEE4] no-underline shadow-[0_18px_40px_rgba(23,19,14,0.22)] md:flex-row md:items-center md:justify-between md:gap-10 md:px-10 md:py-9'
            >
                <div
                    aria-hidden
                    className='pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-[radial-gradient(circle,rgba(244,80,42,0.35)_0%,rgba(244,80,42,0)_70%)]'
                />
                <div className='relative flex flex-col gap-2'>
                    <span className='inline-flex w-fit items-center rounded-full bg-[#F4502A] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-white'>
                        {t('foodCost.pricing.companionship.eyebrow', 'Ponlo en orden')}
                    </span>
                    <p className='m-0 text-[24px] font-medium leading-[1.15] tracking-[-0.6px] md:text-[30px]'>
                        {t('foodCost.pricing.companionship.prompt')}
                    </p>
                    <p className='m-0 max-w-[52ch] text-[15px] leading-[1.55] text-[#C9C0B3]'>
                        {t(
                            'foodCost.pricing.companionship.description',
                            'Una persona te acompaña dentro de Tipi para ponerlo en marcha rápido y entender tus números. Desde 600 € + IVA.',
                        )}
                    </p>
                </div>
                <span className='relative inline-flex min-h-12 shrink-0 items-center justify-center self-start rounded-full bg-[#F5EEE4] px-6 py-3 text-[15px] font-bold text-[#17130E] transition-transform group-hover:translate-x-0.5 md:self-center'>
                    {t('foodCost.pricing.companionship.cta')} →
                </span>
            </LocaleLink>
        </div>
    ) : null

    return (
        <section id='pricing' ref={sectionRef} className='scroll-mt-24 bg-white pb-12 md:pb-20'>
            <div
                className={cn(
                    'mx-auto max-w-[1180px] overflow-visible md:grid md:items-center md:gap-10 md:px-10 md:pt-14',
                    isDirectConversion
                        ? 'md:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:max-w-[1280px]'
                        : 'md:grid-cols-[minmax(0,1.05fr)_minmax(320px,0.95fr)]',
                )}
            >
                <div className='relative h-[220px] overflow-hidden md:h-[480px] md:rounded-[28px]'>
                    <img
                        src={ASSETS.pricingPhoto}
                        alt={t('foodCost.pricing.imageAlt')}
                        loading='lazy'
                        decoding='async'
                        className='absolute inset-0 h-full w-full object-cover object-[center_30%] md:object-center'
                    />
                </div>

                {isDirectConversion ? (
                    <div className='relative z-10 mx-[22px] mt-14 flex max-w-[880px] flex-col gap-7 overflow-visible rounded-[40px] border border-[#E4DCD0] bg-[#F3ECE2] px-5 pb-10 pt-12 text-[#1A1712] sm:px-8 sm:pb-12 md:mx-0 md:mt-8 md:w-full md:px-10 md:pb-14 md:pt-14'>
                        <aside
                            className='absolute right-3 top-0 z-20 flex w-[9.5rem] -translate-y-1/2 rotate-[6deg] flex-col gap-2 rounded-[16px] bg-[#1A1712] px-3.5 py-3 text-[#F3ECE2] shadow-[0_16px_32px_rgba(26,23,18,0.28)] sm:right-4 sm:w-[11rem] sm:px-4 sm:py-3.5 md:-right-6 md:top-0 md:w-[200px] md:gap-2.5 md:rounded-[20px] md:px-5 md:py-4 lg:-right-8 lg:w-[220px]'
                            aria-label={t('foodCost.pricing.sectorBadgeAriaLabel')}
                        >
                            <p className='relative m-0 self-start text-2xl font-extrabold leading-none tracking-[-0.02em] sm:text-[26px] md:text-[28px]'>
                                {t('foodCost.pricing.priceWas')}
                                <span
                                    className='absolute -inset-x-0.5 top-[52%] h-[3px] origin-center -rotate-6 bg-[#F4502A]'
                                    aria-hidden
                                />
                            </p>
                            <p className='m-0 text-[11px] leading-[1.3] sm:text-xs md:text-sm'>
                                {t('foodCost.pricing.sectorBadgeNote')}
                            </p>
                        </aside>

                        <div className='flex flex-col gap-3 pr-[6.5rem] sm:pr-36 md:pr-40'>
                            <div className='self-start text-sm font-bold uppercase leading-[1.5] tracking-[0.14em] text-[#8A7F70] sm:text-base md:text-lg'>
                                <span className='block'>{t('foodCost.pricing.eyebrowLine1')}</span>
                                <span className='block'>{t('foodCost.pricing.eyebrowLine2')}</span>
                            </div>

                            {showIntroOffer ? (
                                <>
                                    <p className='m-0 text-sm font-bold uppercase tracking-[0.14em] text-[#F4502A] md:text-base'>
                                        {t('foodCost.pricing.direct.offerEyebrow')}
                                    </p>
                                    <div className='flex flex-wrap items-baseline gap-2.5 sm:gap-3'>
                                        <span className='text-[52px] font-extrabold leading-[0.95] tracking-[-0.045em] text-[#F4502A] sm:text-[64px] md:text-[72px]'>
                                            {t('foodCost.pricing.direct.offerPrice')}
                                        </span>
                                        <span className='text-[22px] font-bold tracking-[-0.02em] text-[#A79D90] line-through decoration-[3px] sm:text-[28px] md:text-[32px]'>
                                            {t('foodCost.pricing.priceNow')}
                                        </span>
                                        <span className='text-base font-bold text-[#F4502A] sm:text-lg md:text-xl'>
                                            {t('foodCost.pricing.vat')}
                                        </span>
                                    </div>
                                    <p className='m-0 text-pretty text-base font-semibold leading-[1.45] sm:text-lg md:text-xl'>
                                        {t('foodCost.pricing.direct.offerSupport')}
                                    </p>
                                </>
                            ) : (
                                <div className='flex flex-wrap items-baseline gap-2.5 sm:gap-3'>
                                    <span className='text-[52px] font-extrabold leading-[0.95] tracking-[-0.045em] text-[#1A1712] sm:text-[64px] md:text-[72px]'>
                                        {t('foodCost.pricing.priceNow')}
                                    </span>
                                    <span className='text-base font-bold text-[#F4502A] sm:text-lg md:text-xl'>
                                        {t('foodCost.pricing.vat')}
                                    </span>
                                </div>
                            )}
                        </div>

                        {showIntroOffer && offerRemaining && !offerRemaining.isExpired ? (
                            <div
                                role='timer'
                                aria-live='off'
                                className='flex flex-col gap-4 rounded-[28px] border border-[#F0C9BA] bg-[#FBF7F1] px-6 pb-5 pt-6 max-[560px]:px-5 md:px-8'
                            >
                                <p className='m-0 text-sm font-bold uppercase tracking-[0.14em] text-[#8A7F70] md:text-base'>
                                    {t('foodCost.pricing.direct.countdownLabel')}
                                </p>
                                <div className='grid grid-cols-3 gap-3'>
                                    <div className='flex flex-col gap-0.5'>
                                        <span className='text-[40px] font-extrabold tabular-nums leading-none tracking-[-0.03em] max-[560px]:text-[36px] md:text-[44px]'>
                                            {offerHours}
                                        </span>
                                        <span className='text-[15px] text-[#6B6257] md:text-[17px]'>
                                            {t('foodCost.pricing.direct.hours')}
                                        </span>
                                    </div>
                                    <div className='flex flex-col gap-0.5'>
                                        <span className='text-[40px] font-extrabold tabular-nums leading-none tracking-[-0.03em] max-[560px]:text-[36px] md:text-[44px]'>
                                            {offerMinutes}
                                        </span>
                                        <span className='text-[15px] text-[#6B6257] md:text-[17px]'>
                                            {t('foodCost.pricing.direct.minutes')}
                                        </span>
                                    </div>
                                    <div className='flex flex-col gap-0.5'>
                                        <span className='text-[40px] font-extrabold tabular-nums leading-none tracking-[-0.03em] text-[#F4502A] max-[560px]:text-[36px] md:text-[44px]'>
                                            {offerSeconds}
                                        </span>
                                        <span className='text-[15px] text-[#6B6257] md:text-[17px]'>
                                            {t('foodCost.pricing.direct.seconds')}
                                        </span>
                                    </div>
                                </div>
                                <div className='h-2 overflow-hidden rounded-full bg-[#EDE4D8]'>
                                    <div
                                        className='h-full rounded-full bg-[#F4502A] transition-[width] duration-1000 ease-linear'
                                        style={{
                                            width: `${((offerRemaining.progressRatio ?? 0) * 100).toFixed(2)}%`,
                                        }}
                                    />
                                </div>
                            </div>
                        ) : null}

                        <SecondMonthCalculator
                            variant='treatment'
                            locale={locale}
                            numberLocale={numberLocale}
                            hasMounted={hasMounted}
                        />

                        <p className='m-0 text-center text-lg font-semibold text-[#8A7F70]'>
                            {t('foodCost.pricing.direct.ctaPrompt')}
                        </p>

                        <div className='flex items-start gap-3.5 rounded-[20px] bg-[#DFE4D2] px-6 py-[22px] text-[19px] font-semibold leading-[1.5]'>
                            <Check
                                className='mt-1 h-[22px] w-[22px] shrink-0 text-[#2E9A4B]'
                                strokeWidth={3}
                                aria-hidden
                            />
                            <span className='flex flex-col'>
                                <span>{t('foodCost.pricing.direct.guaranteeLine1')}</span>
                                <span>{t('foodCost.pricing.direct.guaranteeLine2')}</span>
                            </span>
                        </div>

                        <a
                            href={pricingAppUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            onClick={trackPricingCta}
                            className='inline-flex h-[66px] items-center justify-center self-center rounded-full bg-[#D0391B] px-9 text-xl font-bold text-white no-underline transition-colors hover:bg-[#B52F14]'
                        >
                            {t('foodCost.pricing.direct.cta')}
                        </a>
                    </div>
                ) : (
                    <div className='relative z-10 mx-[22px] mt-10 flex flex-col gap-[18px] overflow-visible rounded-[28px] border border-[#E2D9CB] bg-[#F5EEE4] px-5 pb-6 pt-8 md:mx-0 md:mt-4 md:px-7 md:pb-8 md:pt-9'>
                        <aside
                            className='border-[#F5EEE4]/12 absolute -right-2 -top-4 z-20 w-[min(100%,10.25rem)] rotate-[9deg] rounded-2xl border bg-[#17130E] px-3.5 py-3 shadow-[0_10px_28px_rgba(23,19,14,0.28)] md:-right-5 md:-top-5'
                            aria-label={t('foodCost.pricing.sectorBadgeAriaLabel')}
                        >
                            <p className='m-0 text-[24px] font-extrabold leading-none tracking-[-0.03em] text-[#F5EEE4] line-through decoration-[#F4502A] decoration-2'>
                                {t('foodCost.pricing.priceWas')}
                            </p>
                            <p className='mb-0 mt-2 text-[11px] font-semibold leading-snug text-[#C9C0B3]'>
                                {t('foodCost.pricing.sectorBadgeNote')}
                            </p>
                        </aside>

                        <div className='pr-[7.5rem] text-[13px] font-bold uppercase tracking-[0.08em] text-[#8A7F6F] md:pr-36'>
                            <span className='block'>{t('foodCost.pricing.eyebrowLine1')}</span>
                            <span className='block'>{t('foodCost.pricing.eyebrowLine2')}</span>
                        </div>

                        <div>
                            <div className='flex flex-wrap items-baseline gap-2.5'>
                                <div className='text-[46px] font-extrabold leading-[0.98] tracking-[-0.035em] text-[#17130E] md:text-[52px]'>
                                    {t('foodCost.pricing.priceNow')}
                                </div>
                                <div className='text-[15px] font-bold text-[#F4502A]'>{t('foodCost.pricing.vat')}</div>
                            </div>
                            <p className='mb-0 mt-2 text-sm leading-[1.55] text-[#5C5548]'>
                                {t('foodCost.pricing.monthlyPrefix')}
                            </p>
                        </div>

                        <div className='flex flex-col gap-1'>
                            <div className='text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8A7F6F]'>
                                {t('foodCost.pricing.includesLabel')}
                            </div>
                            <div className='flex flex-col border-t border-[#E2D9CB]'>
                                {INCLUDE_KEYS.map(includeKey => (
                                    <div
                                        key={includeKey}
                                        className='grid grid-cols-[20px_1fr] items-start gap-2.5 border-b border-[#E2D9CB] py-3'
                                    >
                                        <span className='text-[15px] font-extrabold text-[#F4502A]' aria-hidden>
                                            ✓
                                        </span>
                                        <div className='text-[15px] font-semibold leading-snug text-[#17130E]'>
                                            {t(`foodCost.pricing.includes.${includeKey}`)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <SecondMonthCalculator
                            variant='control'
                            locale={locale}
                            numberLocale={numberLocale}
                            hasMounted={hasMounted}
                        />

                        <div className='text-center text-[13px] font-semibold text-[#8A7F6F]'>
                            {t('foodCost.pricing.ctaPrompt')}
                        </div>

                        <div className='flex items-start gap-2 rounded-xl bg-[rgba(31,168,85,0.12)] px-3 py-2.5 text-sm font-semibold leading-snug text-[#17130E]'>
                            <span className='mt-0.5 font-extrabold text-[#1FA855]' aria-hidden>
                                ✓
                            </span>
                            {t('foodCost.pricing.guarantee')}
                        </div>

                        <a
                            href={pricingAppUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            onClick={trackPricingCta}
                            className='inline-flex min-h-11 items-center justify-center self-center rounded-full bg-[#D13D1A] px-6 py-2.5 text-center text-sm font-bold text-white no-underline'
                        >
                            {t('foodCost.pricing.cta')}
                        </a>
                    </div>
                )}
            </div>
            {companionshipCard}
        </section>
    )
}
